import { DropDownOption } from '../../model/drop-down/DropDownOption';
import { DropDownService } from '../../services/other/drop-down.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { consClusterToLabel, ConstructionCluster } from '../../model/geo/ConstructionCluster';
import { BY_ALPHA_2 } from '../../model/geo/Countries';
import { CountryAlpha2Code } from '../../model/geo/CountryAlpha2Code';
import { devClusterToLabel, DevelopmentCluster } from '../../model/geo/DevelopmentCluster';
import { GlobalRegion, globalRegionToLabel } from '../../model/geo/GlobalRegion';
import { ProjectFilter } from '../../model/ProjectFilter';
import { phaseToLabel, ProjectPhase } from '../../model/general/ProjectPhase';
import { ProjectStage, stageToLabel } from '../../model/general/ProjectStage';
import { ProjectStatus, statusToLabel } from '../../model/general/ProjectStatus';
import { ProjectType, projectTypeToLabel } from '../../model/general/ProjectType';
import { Technology, technologyToLabel } from '../../model/general/Technology';
import { ProjectFilterService } from '../../services/project-list/project-filter.service';
import { ProjectListService } from '../../services/project-list/project-list.service';
import { BusinessModel, businessModelToLabel } from '../../model/general/BusinessModel';
import { RouteToMarket, routeToMarketToLabel } from '../../model/general/RouteToMarket';
import { devDirectorateToLabel, DevelopmentDirectorate } from '../../model/geo/DevelopmentDirectorate';
import {
  HybridProjectFilterOption,
  hybridProjectFilterOptionToLabel
} from '../../model/general/HybridProjectFilterOption';
import { MyProjectsFilterOption, myProjectsFilterOptionToLabel } from '../../model/general/MyProjectsFilterOption';
import {
  ProjectManagingApplication,
  projectManagingApplicationToLabel
} from '../../model/general/ProjectManagingApplication';
import { ProjectValidationFilter, projectValidationFilterToLabel } from '../../model/validation/ProjectValidationFilter';
import { ValidationLockFilter, validationLockFilterToLabel } from '../../model/validation/ValidationLockFilter';

@Component({
  selector: 'digires-project-filter-chips',
  templateUrl: './project-filter-chips.component.html',
  styleUrls: ['./project-filter-chips.component.scss']
})
export class ProjectFilterChipsComponent implements OnInit, OnDestroy {

  private readonly destroy$: Subject<boolean> = new Subject<boolean>();
  private readonly allManagingApps: ProjectManagingApplication[] = Object.values(ProjectManagingApplication);
  private readonly allTechnologies = Object.values(Technology);
  private readonly allTypes = Object.values(ProjectType);
  private readonly allStatuses = Object.values(ProjectStatus);
  private readonly allPhases = Object.values(ProjectPhase);
  private readonly allStages = Object.values(ProjectStage);
  private readonly allGlobalRegions = Object.values(GlobalRegion);
  private readonly allDevCluster = Object.values(DevelopmentCluster);
  private readonly allConsCluster = Object.values(ConstructionCluster);
  private readonly allBusinessModels = Object.values(BusinessModel);
  private readonly allRouteToMarket = Object.values(RouteToMarket);
  private readonly allDevelopmentDirectorates = Object.values(DevelopmentDirectorate);
  private readonly allProjectValidations = Object.values(ProjectValidationFilter);
  private allCountryCodes = Object.values(CountryAlpha2Code);

  idToTeamOption: { [optionId: string]: DropDownOption } = {};
  allTeamIds: string[] = [];
  filterChips: FilterChip[] = [];

  constructor(
    private projectListService: ProjectListService,
    private filterService: ProjectFilterService,
    private dropDownService: DropDownService,
  ) { }

  ngOnInit(): void {
    this.filterService
      .observeProjectFilter()
      .pipe(takeUntil(this.destroy$))
      .subscribe(filter => {
        this.onFilterChanged(filter);
      });
    this.projectListService
      .getUsedCountryCodes()
      .then(countryCodes => {
        this.allCountryCodes = countryCodes;
        this.onFilterChanged(this.filterService.getProjectFilter());
      });

    this.dropDownService.fetchConfig('team-location')
      .then(teamLocations => {
        this.idToTeamOption = {};
        for (const option of teamLocations.values) {
          this.idToTeamOption[option.id] = option;
        }
        this.allTeamIds = Object.keys(this.idToTeamOption);
        this.onFilterChanged(this.filterService.getProjectFilter());
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  private onFilterChanged(filter: ProjectFilter) {
    this.filterChips = [];
    if (filter.projectName && filter.projectName.length > 0) {
      this.filterChips.push({
        value: null, // unused
        label: `Project Name: ${filter.projectName}`,
        onRemove: () => this.filterService.removeNameFilter(),
      });
    }
    if (filter.managerUserId && filter.managerUserId.length > 0) {
      this.filterChips.push({
        value: null, // unused
        label: `Project Manager: ${filter.managerUserId}`,
        onRemove: () => this.filterService.removeProjectManagerFilter(),
      });
    }
    if (filter.region && filter.region.length > 0) {
      for (const region of filter.region) {
        this.filterChips.push({
          value: region,
          label: `Region: ${region}`,
          onRemove: value => this.filterService.removeRegionFilter(value),
        });
      }
    }
    this.createChipsFor(
      'Managing Application',
      filter.managingApplication,
      this.allManagingApps,
      type => projectManagingApplicationToLabel[type],
      value => this.filterService.removeManagingApplicationFilter(value),
    );
    this.createChipsFor(
      'Technology',
      filter.technology,
      this.allTechnologies,
      type => technologyToLabel[type],
      value => this.filterService.removeTechnologyFilter(value),
    );
    this.createChipsFor(
      'Type',
      filter.type,
      this.allTypes,
      type => projectTypeToLabel[type],
      value => this.filterService.removeTypeFilter(value),
    );
    this.createChipsFor(
      'Status',
      filter.status,
      this.allStatuses,
      status => statusToLabel[status],
      value => this.filterService.removeStatusFilter(value),
    );
    this.createChipsFor(
      'Phase',
      filter.phase,
      this.allPhases,
      phase => phaseToLabel[phase],
      value => this.filterService.removePhaseFilter(value),
    );
    this.createChipsFor(
      'Stage',
      filter.stage,
      this.allStages,
      stage => stageToLabel[stage],
      value => this.filterService.removeStageFilter(value),
    );
    this.createChipsFor(
      'Country',
      filter.countryCode,
      this.allCountryCodes,
      countryCode => BY_ALPHA_2[countryCode]?.name || countryCode,
      value => this.filterService.removeCountryFilter(value),
    );
    this.createChipsFor(
      'Global Region',
      filter.globalRegion,
      this.allGlobalRegions,
      globalRegion => globalRegionToLabel[globalRegion],
      value => this.filterService.removeGlobalRegionFilter(value),
    );
    this.createChipsFor(
      'Construction Cluster',
      filter.constructionCluster,
      this.allConsCluster,
      constructionCluster => consClusterToLabel[constructionCluster],
      value => this.filterService.removeConstructionClusterFilter(value),
    );
    this.createChipsFor(
      'Business Model',
      filter.businessModel,
      this.allBusinessModels,
      businessModel => businessModelToLabel[businessModel],
      value => this.filterService.removeBusinessModelFilter(value),
    );
    this.createChipsFor(
      'Route To Market',
      filter.routeToMarket,
      this.allRouteToMarket,
      routeToMarket => routeToMarketToLabel[routeToMarket],
      value => this.filterService.removeRouteToMarketFilter(value),
    );
    this.createChipsFor(
      'Development Directorate',
      filter.developmentDirectorate,
      this.allDevelopmentDirectorates,
      developmentDirectorate => devDirectorateToLabel[developmentDirectorate],
      value => this.filterService.removeDevelopmentDirectorateFilter(value),
    );
    this.createChipsFor(
      'Team',
      filter.teamId,
      this.allTeamIds,
      teamId => this.idToTeamOption[teamId]?.label || teamId,
      value => this.filterService.removeTeamFilter(value),
    );
    if (filter.hybridProject !== HybridProjectFilterOption.ALL_PROJECTS) {
      this.filterChips.push({
        value: null, // unused
        label: hybridProjectFilterOptionToLabel[filter.hybridProject],
        onRemove: () => this.filterService.removeHybridProjectStatusFilter(),
      });
    }
    if (filter.myProjects !== MyProjectsFilterOption.ALL_PROJECTS) {
      this.filterChips.push({
        value: null, // unused
        label: myProjectsFilterOptionToLabel[filter.myProjects],
        onRemove: () => this.filterService.removeMyProjectFilter(),
      });
    }
    this.createChipsFor(
      'Project Validations',
      filter.projectValidation,
      this.allProjectValidations,
      projectValidation => projectValidationFilterToLabel[projectValidation],
      value => this.filterService.removeProjectValidationFilter(value),
    );
    if (filter.validationLock !== ValidationLockFilter.ALL_PROJECTS) {
      this.filterChips.push({
        value: null, // unused
        label: `Validation Lock: ${ validationLockFilterToLabel[filter.validationLock]}`,
        onRemove: () => this.filterService.removeValidationLockFilter(),
      });
    }
  }

  private createChipsFor<X>(
    prefix: string,
    selectedOptions: X[],
    allOptions: X[],
    optionToLabel: (value: X) => string,
    onRemove: (value: X) => void,
  ) {
    if (!selectedOptions || selectedOptions.length === allOptions.length) {
      return;
    }

    for (const opt of selectedOptions) {
      this.filterChips.push({
        value: opt,
        label: `${prefix}: ${optionToLabel(opt)}`,
        onRemove: onRemove,
      });
    }
  }
}

interface FilterChip {
  value: any;
  label: string;
  onRemove: (value: any) => void;
}
